/* eslint-disable jsx-a11y/interactive-supports-focus */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React from 'react';
import _ from 'underscore';
import PropTypes from 'prop-types';
import classNames from 'classnames';

export const AlertType = {
  info: 'info',
  warning: 'warning',
  danger: 'danger',
  success: 'success',
  collapsible: 'collapsible',
} as const;

export type AlertTypeValue = typeof AlertType[keyof typeof AlertType];

export interface InlineAlertProps {
  type: string;
  className?: string;
  children: React.ReactNode;
}

export type InlineAlertVariantProps = Omit<InlineAlertProps, 'type'>;

export interface InlineCollapsibleAlertProps {
  isCollapsed?: boolean;
  collapseCallback?: () => void;
  dismissLinkText?: string;
  dismissCallback?: () => void;
  children: React.ReactNode;
}

export interface InlineCollapsibleAlertState {
  isCollapsed?: boolean;
}

export function InlineAlert(props: InlineAlertProps) {
  return (
    <div className={classNames('alert-spacing', props.className)}>
      <div className={classNames('alert', `alert-${props.type}`)}>
        <div className="alert-image" />
        <div className="alert-content">
          {props.children}
        </div>
      </div>
    </div>
  );
}

(InlineAlert as React.FC<InlineAlertProps>).propTypes = {
  type: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

(InlineAlert as React.FC<InlineAlertProps>).defaultProps = {
  className: undefined,
};

export function InlineInfoAlert(props: InlineAlertVariantProps) {
  return (
    <InlineAlert {...props} type={AlertType.info} />
  );
}

export function InlineWarningAlert(props: InlineAlertVariantProps) {
  return (
    <InlineAlert {...props} type={AlertType.warning} />
  );
}

export function InlineDangerAlert(props: InlineAlertVariantProps) {
  return (
    <InlineAlert {...props} type={AlertType.danger} />
  );
}

export function InlineSuccessAlert(props: InlineAlertVariantProps) {
  return (
    <InlineAlert {...props} type={AlertType.success} />
  );
}

export class InlineCollapsibleAlert extends React.Component<InlineCollapsibleAlertProps, InlineCollapsibleAlertState> {
  constructor(props: InlineCollapsibleAlertProps) {
    super(props);
    this.state = { isCollapsed: props.isCollapsed };
  }
  onCollapseLinkClick = () => this.setState({ isCollapsed: !this.state.isCollapsed }, () => this.props.collapseCallback?.());
  onDismissLinkClick = () => this.props.dismissCallback?.();
  render() {
    const {
      dismissLinkText, children,
    } = this.props;
    return (
      <InlineAlert type={AlertType.collapsible}>
        <div
          role="link"
          className="collapsible-link"
          onClick={this.onCollapseLinkClick}
          onKeyPress={this.onCollapseLinkClick}
        >
          <div className={this.state.isCollapsed ? 'expanderImage' : 'expandedImage'} />
          {children}
        </div>
        {dismissLinkText &&
          <a
            className="dismiss-link"
            onClick={this.onDismissLinkClick}
            onKeyPress={this.onDismissLinkClick}
            aria-label="dismiss-link"
            aria-labelledby="dismiss-link"
            href="javascript:void(0);" // eslint-disable-line no-script-url
          >
            {dismissLinkText}
          </a>}
      </InlineAlert>
    );
  }
}

const InlineAlertPropTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  className: PropTypes.string,
};

const InlineAlertDefaultProps = {
  className: undefined as string | undefined,
};

(InlineInfoAlert as React.FC<InlineAlertVariantProps>).propTypes = InlineAlertPropTypes;
(InlineInfoAlert as React.FC<InlineAlertVariantProps>).defaultProps = InlineAlertDefaultProps;
(InlineWarningAlert as React.FC<InlineAlertVariantProps>).propTypes = InlineAlertPropTypes;
(InlineWarningAlert as React.FC<InlineAlertVariantProps>).defaultProps = InlineAlertDefaultProps;
(InlineDangerAlert as React.FC<InlineAlertVariantProps>).propTypes = InlineAlertPropTypes;
(InlineDangerAlert as React.FC<InlineAlertVariantProps>).defaultProps = InlineAlertDefaultProps;
(InlineSuccessAlert as React.FC<InlineAlertVariantProps>).propTypes = InlineAlertPropTypes;
(InlineSuccessAlert as React.FC<InlineAlertVariantProps>).defaultProps = InlineAlertDefaultProps;


(InlineCollapsibleAlert as React.ComponentClass<InlineCollapsibleAlertProps>).defaultProps = {
  isCollapsed: false,
  collapseCallback: _.noop,
  dismissLinkText: '',
  dismissCallback: _.noop,
};
(InlineCollapsibleAlert as React.ComponentClass<InlineCollapsibleAlertProps>).propTypes = {
  isCollapsed: PropTypes.bool,
  collapseCallback: PropTypes.func,
  dismissLinkText: PropTypes.string,
  dismissCallback: PropTypes.func,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};
